import type {
  RowRelations, SpreadsheetDescriptor,
} from '~/_shared/types/map';
import type { PickAction } from '~/_shared/utils/types/action.type';
import type { GeocodingAction } from '~/store/geocoding/geocoding.action';
import { GEOCODING_SET_PROGRESS } from '~/store/geocoding/geocoding.actionTypes';
import type { MapAction } from '~/store/map/map.action';
import { MAP_RESET } from '~/store/map/map.actionTypes';
import type { MapInfoAction } from '~/store/mapInfo/mapInfo.action';
import { MAP_INFO_FETCH_DATA_SUCCESS } from '~/store/mapInfo/mapInfo.actionTypes';
import { rowRelationsEventReceived } from '~/store/rowRelations/rowRelations.actionCreators';
import type {
  RowRelationsAction, RowRelationsEvent, RowRelationSpreadsheetStatus,
} from '~/store/rowRelations/rowRelations.types';

type RowRelationsState = RowRelations;

const initialState: RowRelationsState = {};

const rowRelationsStatusOverrideMap: Record<RowRelationsEvent['status'], Partial<RowRelationSpreadsheetStatus>> = {
  FAILED: {
    failed: true,
    processing: false,
  },
  FINISHED: {
    failed: false,
    processing: false,
    required: false,
  },
  STARTED: {
    required: true,
    processing: true,
    failed: false,
  },
};

export const rowRelationsReducer = (
  state = initialState,
  action: RowRelationsAction | GeocodingAction | MapInfoAction | MapAction,
): RowRelationsState => {
  if (rowRelationsEventReceived.match(action)) {
    const { event: { realSpreadsheetId, status } } = action.payload;
    const currentSpreadsheetStatus = state[realSpreadsheetId];

    if (currentSpreadsheetStatus) {
      return {
        ...state,
        [realSpreadsheetId]: {
          ...currentSpreadsheetStatus,
          ...rowRelationsStatusOverrideMap[status],
        },
      };
    }
  }

  switch (action.type) {
    case GEOCODING_SET_PROGRESS: {
      const { updates } = action.payload;
      return Object.values(updates).reduce((state, update) => {
        const currentSpreadsheetStatus = state[update.realSpreadsheetId];
        if (currentSpreadsheetStatus) {
          return {
            ...state,
            [update.realSpreadsheetId]: {
              ...currentSpreadsheetStatus,
              required: true,
            },
          };
        }

        return state;
      }, state);
    }
    case MAP_INFO_FETCH_DATA_SUCCESS: {
      return loadOngoingProgress(state, action);
    }
    case MAP_RESET: {
      return initialState;
    }
    default: {
      return state;
    }
  }
};

function loadOngoingProgress(state: RowRelationsState,
  action: PickAction<MapInfoAction, typeof MAP_INFO_FETCH_DATA_SUCCESS>): RowRelationsState {
  const spreadsheets: ReadonlyArray<SpreadsheetDescriptor> | undefined = action.payload.data.spreadsheets;
  const spreadsheetData: Maybe<SpreadsheetDescriptor> = spreadsheets?.find(spreadsheet => spreadsheet.isPrimary);

  const rowRelations = spreadsheetData?.rowRelations;
  if (!spreadsheetData || !rowRelations) {
    return state;
  }
  return rowRelations;
}
